import { authClient } from '@/api/client/auth.client';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { captureError } from '@/shared/lib/errors/capture';
import { AxiosError } from 'axios';
import posthog from 'posthog-js';

export type SendOtpResult =
  | { kind: 'sent' }
  | { kind: 'bounced'; redirectTo: string }
  | { kind: 'rate_limited' }
  | { kind: 'error'; message: string };

function isRateLimited(err: unknown): err is AxiosError {
  return err instanceof AxiosError && err.response?.status === 429;
}

// Only a definite "no active subscription" may bounce. When the check itself
// fails (its BE throttle is 3/min, so a visitor coming LP → /choose-plan →
// /sign-in can exhaust it) the code is sent anyway: a subscriber locked out of
// sign-in costs more than the orphan account an unsubscribed email could create.
async function hasNoSubscription(email: string): Promise<boolean> {
  try {
    const { can_checkout } = await authClient.subscriptionCheck(email);
    return can_checkout;
  } catch (err) {
    captureError(err, {
      component: 'SignInOtpHelper',
      action: 'subscription-check',
      severity: 'warning',
      ...(isRateLimited(err) ? { tags: { signal: 'noise' } } : {}),
    });
    return false;
  }
}

/**
 * Subscription-check gate + OTP send for sign-in. Shared between the manual
 * email-form submit and the page-level auto-fire that runs when ?email=
 * is prefilled from the LP forwarder. Returns a discriminated union so each
 * caller owns its own UI surface (inline form error vs page-level toast).
 *
 * Rate-limit and unknown errors are captured here with appropriate severity
 * (rate-limit is tagged signal=noise per docs/rules/sentry.md) so callers
 * don't have to repeat the noise rules.
 */
export async function sendOtpForSignIn(email: string): Promise<SendOtpResult> {
  try {
    if (await hasNoSubscription(email)) {
      posthog.capture(
        AnalyticsEvent.AUTH_OTP_BLOCKED_NO_SUBSCRIPTION,
        { email_domain: email.split('@')[1] ?? null },
        { send_instantly: true }
      );
      return {
        kind: 'bounced',
        redirectTo: `/choose-plan?email=${encodeURIComponent(email)}`,
      };
    }
    await authClient.otpSend(email);
    posthog.capture(AnalyticsEvent.AUTH_OTP_SENT, {
      email_domain: email.split('@')[1] ?? null,
    });
    return { kind: 'sent' };
  } catch (err) {
    if (isRateLimited(err)) {
      captureError(err, {
        component: 'SignInOtpHelper',
        action: 'otp-send',
        severity: 'warning',
        tags: { signal: 'noise' },
        skipPostHog: true,
      });
      return { kind: 'rate_limited' };
    }
    if (err instanceof AxiosError && err.response?.data?.message) {
      return { kind: 'error', message: err.response.data.message };
    }
    captureError(err, { component: 'SignInOtpHelper', action: 'otp-send' });
    return { kind: 'error', message: 'Something went wrong. Please try again.' };
  }
}
